import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { ModifiedTimelineContainer } from './manual-uploads';
import { ManualUploadItem } from './skyview-data';

interface Props {
  item: ManualUploadItem;
  timelineContainers: ModifiedTimelineContainer[];
  onClose: (wasDeleted: boolean) => void;
  onDelete: (item: ManualUploadItem) => Promise<void>;
}

enum Status {
  Idle,
  Pending,
  Error,
}

const DeleteSkyViewDataDialog = (props: Props) => {
  const { t } = useTranslation('components');
  const [status, setStatus] = React.useState(Status.Idle);
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const isConnectedToTimeline = (item: ManualUploadItem) => {
    // TODO: This check should be replaced with API-level error
    // handling. When the error is returned it will
    // show the correct error message and notification
    return (
      props.timelineContainers.find(
        (container) =>
          container.gltftScanId === item.id ||
          container.tiledModelId === item.id ||
          container.orthoTileId === item.id ||
          container.pointCloudId === item.id,
      ) !== undefined
    );
  };

  const onDelete = async () => {
    setStatus(Status.Pending);

    if (isConnectedToTimeline(props.item)) {
      publishNotification('GEOerr008');
      setErrorMessage(t('manualUploads.deleteDialog.timelineError', { ns: 'components' }));
      setStatus(Status.Error);
      return;
    }

    try {
      await props.onDelete(props.item);
      publishNotification('succ0503', { data: [props.item.name] });
      props.onClose(true);
    } catch (err) {
      publishNotification('GEOerr009');
      setErrorMessage(t('manualUploads.deleteDialog.genericError', { ns: 'components' }));
      setStatus(Status.Error);
    }
  };

  return (
    <>
      <Dialog
        closeIcon={status !== Status.Pending}
        closeOnDimmerClick={status !== Status.Pending}
        useGoogleTranslate={true}
        width={500}
        onClose={() => props.onClose(false)}
      >
        {{
          header: t('manualUploads.deleteDialog.header', { ns: 'components' }),
          content: (
            <Content>
              <Text topMargin={false}>
                <Trans
                  components={{ bold: <strong /> }}
                  i18nKey="manualUploads.deleteDialog.question"
                  ns="components"
                  values={{ name: props.item.name }}
                />
              </Text>
              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                {t('manualUploads.deleteDialog.warning', { ns: 'components' })}
              </InfoBox>
              {status === Status.Error && (
                <InfoBox color="red">
                  {errorMessage ??
                    t('manualUploads.deleteDialog.genericError', { ns: 'components' })}
                </InfoBox>
              )}
            </Content>
          ),
          footer: {
            right: (
              <>
                <Button
                  color="error"
                  disabled={status === Status.Pending}
                  loading={status === Status.Pending}
                  variant="contained"
                  onClick={onDelete}
                >
                  {t('delete', { ns: 'common' })}
                </Button>

                <Button
                  disabled={status === Status.Pending}
                  variant="text"
                  onClick={() => props.onClose(false)}
                >
                  {t('close', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    </>
  );
};

const Text = styled.span<{ topMargin?: boolean; bottomMargin?: boolean }>`
  display: block;

  strong {
    font-weight: 600;
  }

  margin-top: ${(props) => (props.topMargin ? '0.5em' : undefined)};
  margin-bottom: ${(props) => (props.bottomMargin ? '0.5em' : undefined)};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { DeleteSkyViewDataDialog };
